import { useGlobal } from 'context/global/GlobalContext';
import { useRecaptchaExecute } from 'context/RecaptchaContext';
import { useCallback, useEffect, useState } from 'react';
import { useAPI } from 'utils/api';

const RECAPTCHA_HUMAN_THRESHOLD = 0.5;

const useRecaptchaV3 = () => {
  const [isVerified, setVerified] = useState<boolean>(true);
  const { productDetails } = useGlobal();
  const { reCaptchaScore = RECAPTCHA_HUMAN_THRESHOLD } =
    productDetails?.brand || {};

  const executeRecaptcha = useRecaptchaExecute();
  const onRecaptchaSuccess = useCallback(
    (response: { score: number }) => {
      if (response.score < reCaptchaScore) {
        setVerified(false);
      }
    },
    [reCaptchaScore]
  );

  const onError = useCallback(() => {}, []);

  const [verifyCaptcha] = useAPI({
    endpoint: 'auth/verify-captcha',
    method: 'POST',
    onSuccess: onRecaptchaSuccess,
    onError: onError,
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) return;

    try {
      const captchaToken = await executeRecaptcha();

      if (captchaToken) {
        verifyCaptcha({ token: captchaToken });
      }
    } catch {
      // Provider not ready yet — will retry when context updates
    }
  }, [executeRecaptcha, verifyCaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  return isVerified;
};

export function useRecaptchaV3Token() {
  const executeRecaptcha = useRecaptchaExecute();
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    if (!executeRecaptcha) return;

    const getToken = async () => {
      try {
        const token = await executeRecaptcha();
        setToken(token);
      } catch {
        // Provider not ready yet — will retry when context updates
      }
    };
    getToken();
  }, [executeRecaptcha]);
  return { token };
}

export default useRecaptchaV3;
